import React, { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import toast from "react-hot-toast";

const ConfigureRoom = ({ setLoadConfig, roomData }) => {
  const [load, setLoad] = useState(false);
  const [beds, setBeds] = useState([]);
  const [sharingType, setSharingType] = useState(""); // State for tracking sharing type
  const {
    register,
    handleSubmit,
    setValue, // For setting input value dynamically
    formState: { errors },
  } = useForm();

  const handleRoomSubmit = (data) => {
    const maxBeds = sharingType;

    if (data.sharingType < data.numberOfBeds) {
      toast.error(
        `Maximum ${maxBeds} beds allowed for a ${sharingType}-sharing room`
      );
      setValue("numberOfBeds", maxBeds); // Reset the value to the maximum allowed
      return;
    }
    setLoad(true);
    const myData = { ...data, ...roomData };
    console.log("Beds: ", beds);

    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/rooms/register_room`,
      headers: {
        "Content-Type": "application/json",
      },
      data: { ...myData, beds: beds },
    };

    axios
      .request(config)
      .then((response) => {
        console.log(response.data);
        toast.success(response.data.message);
        setLoadConfig(false);
        setLoad(false);
      })
      .catch((error) => {
        console.log(error);
        setLoad(false);
        toast.error(error.response.data.message);
      });
  };

  const handleBedsChange = (e) => {
    const bedCount = parseInt(e.target.value, 10);

    // Determine the maximum beds based on sharing type
    const maxBeds = sharingType;

    if (bedCount > maxBeds) {
      toast.error(
        `Maximum ${maxBeds} beds allowed for a ${sharingType}-sharing room`
      );
      setValue("numberOfBeds", maxBeds); // Reset the value to the maximum allowed
    }

    const bedsList = Array.from(
      { length: bedCount <= maxBeds ? bedCount : maxBeds },
      (_, idx) => ({
        bedNumber: `B-F${roomData?.floorNumber}R${roomData?.roomNumber}${String(
          idx + 1
        ).padStart(2, "0")}`,
      })
    );

    setBeds(bedsList);
  };

  return (
    <div className="fixed inset-0 z-50 w-full bg-[#00000077] flex items-center justify-center p-4">
      <div className="w-[33rem] bg-white p-6 rounded-md shadow-md">
        <h1 className="text-center font-bold mb-4 text-xl ">Configure Room</h1>
        <h2 className="text-lg font-bold tracking-wide flex items-center justify-between mb-6">
          <span className="float-right">
            Room No : R-F{roomData.floorNumber}
            {String(roomData?.roomNumber).padStart(3, "0")}
          </span>
          <span className="float-right">Floor No : {roomData.floorNumber}</span>
        </h2>

        <form onSubmit={handleSubmit(handleRoomSubmit)} className="space-y-4">
          {/* Sharing Type  */}
          <div>
            <label className="block text-gray-700">Sharing Capacity</label>
            <input
              type="text"
              placeholder="Enter sharing capacity"
              // disabled={sharingType ? false : true}
              {...register("sharingType", {
                required: "Sharing Capacity  is required",
              })}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, ""); //Doesn't allow any non-numeric value

                if (e.target.value === "") {
                  setSharingType("");
                  return;
                } //Allows backspacing

                if (e.target.value < 1) {
                  e.target.value = 1; // Prevent values below 1
                  toast.error("Minimum sharing type is 1");
                } else if (e.target.value > 10) {
                  e.target.value = 10; // Prevent values above 10
                  toast.error("Maximum sharing type is 10");
                }
                setSharingType(e.target.value);
              }}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            />
            {errors.sharingType && (
              <p className="text-red-500 text-sm mt-1">
                {errors.sharingType.message}
              </p>
            )}
          </div>

          {/* Number of Beds */}
          <div>
            <label className="block text-gray-700">Total Number of Beds</label>
            <input
              type="text"
              min={1}
              placeholder={
                sharingType
                  ? "Enter Number of Beds"
                  : "Please enter sharing capacity first"
              }
              disabled={sharingType ? false : true}
              {...register("numberOfBeds", {
                required: "Number of beds is required",
              })}
              onChange={handleBedsChange}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, ""); //Doesn't allow any non-numeric value
              }}
              className={`mt-1 block w-full p-2 border border-gray-300 rounded-md ${
                sharingType ? "" : "bg-gray-200 cursor-not-allowed"
              }`}
            />
            {errors.numberOfBeds && (
              <p className="text-red-500 text-sm mt-1">
                {errors.numberOfBeds.message}
              </p>
            )}
          </div>
          {/* Bed Numbers  */}
          <div>
            <span>Bed Numbers: </span>
            <div className="flex flex-wrap gap-2 items-center">
              {beds.map((each, index) => (
                <div
                  className="p-1 px-2 w-fit flex items-center gap-2  rounded-md mx-1 text-white font-semibold bg-primaryBtnHoverColor"
                  key={index}
                >
                  {each.bedNumber}
                </div>
              ))}
            </div>
          </div>

          {/* Bed Rent  */}
          <div>
            <label className="block text-gray-700">Bed Rent</label>
            <input
              type="text"
              min={1}
              placeholder="Enter bed rent"
              {...register("bedRent", { required: "Bed Rent is required" })}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, ""); //Doesn't allow any non-numeric value
              }}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Facilities */}
          <div>
            <label className="block text-gray-700">Facilities</label>
            <div className="mt-1 space-y-2 grid grid-cols-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value="Air Conditioner"
                  {...register("facilities")}
                  className="h-4 w-4"
                />
                <span>Air Conditioner</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value="Attached Toilet"
                  {...register("facilities")}
                  className="h-4 w-4"
                />
                <span>Attached Toilet</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value="Attached Balcony"
                  {...register("facilities")}
                  className="h-4 w-4"
                />
                <span>Attached Balcony</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value="Geyser"
                  {...register("facilities")}
                  className="h-4 w-4"
                />
                <span>Geyser</span>
              </label>
            </div>
            {errors.facilities && (
              <p className="text-red-500 text-sm mt-1">
                {errors.facilities.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex items-center gap-3 text-xl">
            <button
              onClick={() => {
                setLoadConfig(false);
              }}
              type="button"
              className="w-full py-2 rounded-md transition duration-200 border hover:bg-secondaryTextColor"
            >
              Close
            </button>
            <button
              type="submit"
              className="w-full bg-primaryBtnColor text-white py-2 rounded-md hover:bg-primaryBtnHoverColor transition duration-200"
            >
              {load ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigureRoom;
